/**
 * <PinchZoom> — a gesture-driven zoom/pan/rotate container (atom, L0.1).
 *
 * The maison, isolation-safe port of the legacy `zoom.tsx` engine (`src/common/components/zoom`,
 * used by `MaterialDisplayModal`). It wraps arbitrary `children` (an `<Image>`, in practice) in a
 * `react-native-reanimated` + `react-native-gesture-handler` surface that supports:
 *   - focal-point pinch-to-zoom (clamped to `[minimumZoomScale, maximumZoomScale]`),
 *   - pan while zoomed (bounded to the content edges),
 *   - an imperative `zoomIn / zoomOut / rotate` ref (`PinchZoomHandle`) for on-screen buttons.
 *
 * Unlike `<ScrollView maximumZoomScale>` (iOS-only), this works on web, iOS and Android — which is
 * why it replaces the ScrollView zoom inside `<MediaDisplay>`.
 *
 * Styling note: PinchZoom is a *transform* wrapper — it reads NO theme tokens and paints NO colors.
 * Its only non-Unistyles style is the reanimated `useAnimatedStyle` (the transform plus the fill,
 * shared-value driven, per-frame, token-free), which by nature cannot go through `StyleSheet.create`.
 * There is nothing to route through `theme.*`, so this is a legitimate exception to the Unistyles-only
 * rule and it passes `ds:inventory` (zero hex).
 *
 * The caller's `style` goes on a plain outer `View`, never on the `Animated.View` (DS `CLAUDE.md`, rule 1:
 * no Unistyles style on a reanimated view). The animated layer fills the outer view's content box and
 * pivots on its centre.
 *
 * Requires an ancestor `<GestureHandlerRootView>` (present at the app root in `App.js`, and injected
 * into the Storybook preview decorator).
 */
import React, { useCallback, useImperativeHandle, useMemo } from "react"
import { View, type LayoutChangeEvent, type ViewProps, type ViewStyle } from "react-native"
import Animated, {
    cancelAnimation,
    runOnJS,
    useAnimatedStyle,
    useDerivedValue,
    useSharedValue,
    withTiming,
} from "react-native-reanimated"
import { Gesture, GestureDetector, type GestureType } from "react-native-gesture-handler"

/** Imperative controls exposed via `ref` — wired to the viewer's zoom/rotate buttons. */
export type PinchZoomHandle = {
    zoomIn: () => void
    zoomOut: () => void
    rotate: () => void
}

export type PinchZoomProps = {
    children: React.ReactNode
    /** Lower bound of the pinch scale. Defaults to 1. */
    minimumZoomScale?: number
    /** Upper bound of the pinch scale. Defaults to 4. */
    maximumZoomScale?: number
    /** External gesture to run simultaneously (e.g. a parent scroll). Optional. */
    simultaneousGesture?: GestureType
    /** Fires when the content first goes above scale 1. */
    onZoomBegin?: () => void
    /** Fires when the content returns to scale 1. */
    onZoomEnd?: () => void
    /** Toggle all gestures off (e.g. while drawing an annotation over the image). Defaults to true. */
    gesturesEnabled?: boolean
} & ViewProps

export const PinchZoom = React.forwardRef<PinchZoomHandle, PinchZoomProps>((props, ref) => {
    const {
        children,
        minimumZoomScale = 1,
        maximumZoomScale = 4,
        style: propStyle,
        onZoomBegin,
        onZoomEnd,
        simultaneousGesture,
        gesturesEnabled = true,
        ...rest
    } = props

    const translationX = useSharedValue(0)
    const translationY = useSharedValue(0)
    const originX = useSharedValue(0)
    const originY = useSharedValue(0)
    const scale = useSharedValue(1)
    const isPinching = useSharedValue(false)
    const isZoomed = useSharedValue(false)
    const viewHeight = useSharedValue(0)
    const viewWidth = useSharedValue(0)

    const prevScale = useSharedValue(0)
    const offsetScale = useSharedValue(0)
    const prevTranslationX = useSharedValue(0)
    const prevTranslationY = useSharedValue(0)

    const panTranslateX = useSharedValue(0)
    const panTranslateY = useSharedValue(0)

    const rotate = useSharedValue(0)
    const scaleAdjustment = useSharedValue(1)

    // JS-thread reset used by the imperative zoomOut() button.
    const resetZoom = useCallback(() => {
        translationX.value = withTiming(0)
        translationY.value = withTiming(0)
        scale.value = withTiming(1)
        originX.value = 0
        originY.value = 0
        isPinching.value = false
        prevScale.value = 0
        prevTranslationX.value = 0
        prevTranslationY.value = 0
        panTranslateX.value = 0
        panTranslateY.value = 0
        // eslint-disable-next-line react-hooks/exhaustive-deps -- shared values are stable
    }, [])

    useImperativeHandle(
        ref,
        () => ({
            zoomIn: () => {
                if (scale.value < 5) scale.value = withTiming(scale.value + 0.5)
            },
            zoomOut: () => {
                resetZoom()
            },
            rotate: () => {
                rotate.value = withTiming(
                    rotate.value % 90 === 0 ? rotate.value + 90 : 90 * Math.ceil(rotate.value / 90)
                )
                scaleAdjustment.value = rotate.value % 360 === 0 ? 0.7 : rotate.value % 360 === 180 ? 0.7 : 1

                const height = viewHeight.value ?? 1
                const width = viewWidth.value ?? 1
                viewHeight.value = rotate.value % 180 === 0 ? width : height
                viewWidth.value = rotate.value % 180 === 0 ? height : width
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- shared values are stable
        [resetZoom]
    )

    const gesture = useMemo(() => {
        const resetZoomState = () => {
            "worklet"
            translationX.value = withTiming(0)
            translationY.value = withTiming(0)
            scale.value = withTiming(1)
            originX.value = 0
            originY.value = 0
            isPinching.value = false
            prevScale.value = 0
            prevTranslationX.value = 0
            prevTranslationY.value = 0
            panTranslateX.value = 0
            panTranslateY.value = 0
        }

        // Pan is only meaningful once zoomed; while pinching we track the raw finger delta.
        const pan = Gesture.Pan()
            .enabled(gesturesEnabled)
            .onStart(() => {
                "worklet"
                if (isPinching.value || !isZoomed.value) return

                cancelAnimation(translationX)
                cancelAnimation(translationY)
                cancelAnimation(scale)

                prevTranslationX.value = translationX.value
                prevTranslationY.value = translationY.value
            })
            .onUpdate((e) => {
                "worklet"
                if (isPinching.value || !isZoomed.value) {
                    panTranslateX.value = e.translationX
                    panTranslateY.value = e.translationY
                } else {
                    // At scale S the content grows by S; clamp the pan so edges stay in view.
                    const maxTranslateX = (viewWidth.value / 2) * scale.value - viewWidth.value / 2
                    const minTranslateX = -maxTranslateX

                    const maxTranslateY = (viewHeight.value / 2) * scale.value - viewHeight.value / 2
                    const minTranslateY = -maxTranslateY

                    const nextTranslateX = prevTranslationX.value + e.translationX - panTranslateX.value
                    const nextTranslateY = prevTranslationY.value + e.translationY - panTranslateY.value

                    if (nextTranslateX > maxTranslateX) {
                        translationX.value = maxTranslateX
                    } else if (nextTranslateX < minTranslateX) {
                        translationX.value = minTranslateX
                    } else {
                        translationX.value = nextTranslateX
                    }

                    if (nextTranslateY > maxTranslateY) {
                        translationY.value = maxTranslateY
                    } else if (nextTranslateY < minTranslateY) {
                        translationY.value = minTranslateY
                    } else {
                        translationY.value = nextTranslateY
                    }
                }
            })
            .onEnd(() => {
                "worklet"
                if (isPinching.value || !isZoomed.value) return

                panTranslateX.value = 0
                panTranslateY.value = 0

                prevTranslationX.value = translationX.value
                prevTranslationY.value = translationY.value
            })

        const pinch = Gesture.Pinch()
            .enabled(gesturesEnabled)
            .onStart(() => {
                "worklet"
                cancelAnimation(translationX)
                cancelAnimation(translationY)
                cancelAnimation(scale)
                prevScale.value = scale.value
                offsetScale.value = scale.value
            })
            .onUpdate((e) => {
                "worklet"
                // A single pointer mid-gesture means the pinch is ending — stop translating.
                if (e.numberOfPointers === 1 && isPinching.value) {
                    prevTranslationX.value = translationX.value
                    prevTranslationY.value = translationY.value
                    isPinching.value = false
                } else if (e.numberOfPointers === 2) {
                    const newScale = prevScale.value * e.scale

                    if (newScale < minimumZoomScale || newScale > maximumZoomScale) return

                    scale.value = prevScale.value * e.scale

                    // Lock the focal origin on the first two-finger frame.
                    if (!isPinching.value) {
                        isPinching.value = true
                        originX.value = e.focalX
                        originY.value = e.focalY
                        prevTranslationX.value = translationX.value
                        prevTranslationY.value = translationY.value
                        offsetScale.value = scale.value
                    }

                    if (isPinching.value) {
                        // Translate toward the focal point as the scale changes.
                        translationX.value =
                            prevTranslationX.value +
                            -1 * ((scale.value - offsetScale.value) * (originX.value - viewWidth.value / 2))
                        translationY.value =
                            prevTranslationY.value +
                            -1 * ((scale.value - offsetScale.value) * (originY.value - viewHeight.value / 2))
                    }
                }
            })
            .onEnd(() => {
                "worklet"
                isPinching.value = false
                prevTranslationX.value = translationX.value
                prevTranslationY.value = translationY.value

                if (scale.value < 1.1) {
                    resetZoomState()
                }
            })

        return simultaneousGesture
            ? Gesture.Simultaneous(pan, pinch, simultaneousGesture)
            : Gesture.Simultaneous(pan, pinch)

        // eslint-disable-next-line react-hooks/exhaustive-deps -- shared values are stable
    }, [maximumZoomScale, minimumZoomScale, simultaneousGesture, gesturesEnabled])

    useDerivedValue(() => {
        if (scale.value > 1 && !isZoomed.value) {
            isZoomed.value = true
            if (onZoomBegin) runOnJS(onZoomBegin)()
        } else if (scale.value === 1 && isZoomed.value) {
            isZoomed.value = false
            if (onZoomEnd) runOnJS(onZoomEnd)()
        }
    }, [onZoomBegin, onZoomEnd])

    const animatedStyle = useAnimatedStyle(() => {
        return {
            // Fill the outer view on both axes, whatever its `alignItems` / `flexDirection`.
            flex: 1,
            alignSelf: "stretch",
            transform: [
                { translateX: translationX.value },
                { translateY: translationY.value },
                { scale: scale.value * scaleAdjustment.value },
                { rotate: `${rotate.value}deg` },
            ] as ViewStyle["transform"],
        }
    }, [])

    // Measures the animated layer itself: the pan clamp and the pinch focal math work in its frame.
    const measureContent = useCallback(
        (e: LayoutChangeEvent) => {
            viewWidth.value = e.nativeEvent.layout.width
            viewHeight.value = e.nativeEvent.layout.height
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- shared values are stable
        []
    )

    return (
        <View {...rest} style={propStyle}>
            <GestureDetector gesture={gesture}>
                <Animated.View onLayout={measureContent} style={animatedStyle}>
                    {children}
                </Animated.View>
            </GestureDetector>
        </View>
    )
})

PinchZoom.displayName = "PinchZoom"
